import type { ChangeEvent, FC } from "react"
import { useRef, useState } from "react"

import {
  ArrowPathIcon,
  ArrowUpTrayIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline"

import type {
  ArcadiaGreenButtonCsvPreviewResultDto,
  ArcadiaGreenButtonDiagnosticDto,
} from "~/api/dto/arcadia-green-button.dto"
import {
  useArcadiaGreenButtonSupportDiagnostic,
  useCommitArcadiaGreenButtonCsvRecovery,
  usePreviewArcadiaGreenButtonCsvRecovery,
  useRecoverArcadiaGreenButtonSupportConnection,
} from "~/api/hooks/useArcadiaGreenButtonSupport"
import { parseAxiosError } from "~/utils/parseAxiosError"

type GreenbuttonSupportDiagnosticsProps = {
  connectionId: string
  siteId: string
}

const STAGE_LABELS: Record<ArcadiaGreenButtonDiagnosticDto["stage"], string> = {
  webhook_correlation: `Webhook correlation`,
  credential: `Credential validation`,
  meter_discovery: `Meter discovery`,
  meter_selection: `Meter selection`,
  activation: `Interval activation`,
  polling: `Interval polling`,
  interval_import: `Interval import`,
  complete: `Complete`,
  terminal: `Terminal`,
}

const formatDateTime = (value: string | null): string =>
  value
    ? new Intl.DateTimeFormat(`en-US`, {
        dateStyle: `medium`,
        timeStyle: `short`,
      }).format(new Date(value))
    : `Not yet`

const humanizeCode = (code: string): string => code.replaceAll(`_`, ` `)

const ArcadiaCsvPreviewSummary: FC<{
  preview: ArcadiaGreenButtonCsvPreviewResultDto
}> = ({ preview }) => {
  if (!preview.canImport) {
    return (
      <div className="rounded-lg border border-danger/30 bg-danger/5 p-4 text-sm">
        <p className="font-semibold text-danger">
          {preview.rejectionReason
            ? `This file cannot be imported: ${humanizeCode(preview.rejectionReason)}.`
            : `This file cannot be imported.`}
        </p>
      </div>
    )
  }

  const coverage =
    preview.coverageStartAt && preview.coverageEndAt
      ? `${formatDateTime(preview.coverageStartAt)} – ${formatDateTime(preview.coverageEndAt)}`
      : `Coverage unavailable`

  return (
    <div className="rounded-lg border border-border bg-background p-4 text-sm">
      <p className="font-semibold text-foreground">
        Ready to import — review before confirming
      </p>
      <dl className="mt-3 grid grid-cols-2 gap-3">
        <div>
          <dt className="font-semibold text-muted">Provider</dt>
          <dd className="mt-1 font-semibold text-foreground">
            {preview.providerName ?? `Unavailable`}
          </dd>
        </div>
        <div>
          <dt className="font-semibold text-muted">Quality</dt>
          <dd className="mt-1 font-semibold text-foreground">
            {preview.quality ?? `Unavailable`}
          </dd>
        </div>
        <div>
          <dt className="font-semibold text-muted">Coverage</dt>
          <dd className="mt-1 font-semibold text-foreground">{coverage}</dd>
        </div>
        <div>
          <dt className="font-semibold text-muted">Rows</dt>
          <dd className="mt-1 font-semibold text-foreground">
            {preview.rowCount?.toLocaleString() ?? `Unavailable`}
          </dd>
        </div>
      </dl>

      {preview.proposedRepair ? (
        <p className="mt-3 text-muted">
          {`Vision will repair ${preview.proposedRepair.intervalCount.toLocaleString()} interval(s) using ${preview.proposedRepair.methods.map(humanizeCode).join(`, `)}.`}
        </p>
      ) : null}

      {preview.warnings.length > 0 ? (
        <ul className="mt-3 list-inside list-disc text-muted">
          {preview.warnings.map((warning) => (
            <li key={warning}>{humanizeCode(warning)}</li>
          ))}
        </ul>
      ) : null}
    </div>
  )
}

const ArcadiaCsvRecoverySection: FC<{
  connectionId: string
  siteId: string
}> = ({ connectionId, siteId }) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const [file, setFile] = useState<File | null>(null)
  const preview = usePreviewArcadiaGreenButtonCsvRecovery()
  const commit = useCommitArcadiaGreenButtonCsvRecovery()
  const isBusy = preview.isLoading || commit.isLoading
  const committedOk = commit.isSuccess && commit.data.ok

  const resetSelection = () => {
    setFile(null)
    preview.reset()
    commit.reset()
    if (inputRef.current) {
      inputRef.current.value = ``
    }
  }

  const handleChooseFile = () => {
    if (isBusy) {
      return
    }
    inputRef.current?.click()
  }

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const selected = event.target.files?.[0]
    event.target.value = ``
    if (!selected) {
      return
    }

    setFile(selected)
    commit.reset()
    preview.mutate({
      connectionId,
      file: selected,
      requestId: crypto.randomUUID(),
      siteId,
    })
  }

  const handleConfirmImport = () => {
    if (!file || !preview.data?.canImport || commit.isLoading) {
      return
    }

    commit.mutate({
      connectionId,
      expectedContentHash: preview.data.contentHash,
      expectedEvidenceHash: preview.data.evidenceHash ?? ``,
      file,
      requestId: crypto.randomUUID(),
      siteId,
    })
  }

  const previewErrorMessage = preview.isError
    ? parseAxiosError(
        preview.error,
        `The file could not be validated. Try again.`,
      )
    : null
  const commitErrorMessage = commit.isError
    ? parseAxiosError(
        commit.error,
        `The recovery import could not be created. Try again.`,
      )
    : null

  return (
    <div className="mt-5 rounded-lg border border-border bg-background p-4">
      <h4 className="text-sm font-bold text-foreground">Arcadia CSV recovery</h4>
      <p className="mt-1 text-sm text-muted">
        Upload the Arcadia raw-interval CSV export for the selected meter. Vision
        validates it and creates a recovery import for review — this never
        changes the current proposal.
      </p>

      <input
        ref={inputRef}
        type="file"
        accept=".csv,text/csv"
        className="sr-only"
        aria-label="Arcadia CSV file"
        disabled={isBusy}
        onChange={handleFileChange}
      />

      {!file ? (
        <button
          type="button"
          disabled={isBusy}
          className="mt-4 inline-flex min-h-11 items-center justify-center gap-2 rounded-lg border border-border bg-background px-4 py-2 font-display text-sm font-bold text-foreground shadow-sm transition-colors hover:bg-surface disabled:cursor-not-allowed disabled:text-muted"
          onClick={handleChooseFile}
        >
          <ArrowUpTrayIcon aria-hidden="true" className="size-5" />
          Upload Arcadia CSV
        </button>
      ) : (
        <div className="mt-4 space-y-4">
          {preview.isLoading ? (
            <p className="text-sm font-semibold text-muted">
              Validating the uploaded file...
            </p>
          ) : previewErrorMessage ? (
            <p className="text-sm font-semibold text-danger">
              {previewErrorMessage}
            </p>
          ) : preview.data ? (
            <ArcadiaCsvPreviewSummary preview={preview.data} />
          ) : null}

          {committedOk ? (
            <p aria-live="polite" className="text-sm font-semibold text-success">
              Recovery import created for review. This has not changed the
              current proposal.
              {commit.data.duplicate ? ` (already requested)` : ``}
            </p>
          ) : commit.isSuccess && !commit.data.ok ? (
            <p aria-live="polite" className="text-sm font-semibold text-danger">
              {commit.data.rejectionReason
                ? `The file could not be recovered: ${humanizeCode(commit.data.rejectionReason)}.`
                : `The file could not be recovered.`}
            </p>
          ) : commitErrorMessage ? (
            <p aria-live="polite" className="text-sm font-semibold text-danger">
              {commitErrorMessage}
            </p>
          ) : null}

          <div className="flex flex-wrap gap-2">
            {preview.data?.canImport && !committedOk ? (
              <button
                type="button"
                aria-busy={commit.isLoading}
                disabled={commit.isLoading}
                className="inline-flex min-h-11 items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2 font-display text-sm font-bold text-primary-foreground shadow-sm transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:bg-surface disabled:text-muted"
                onClick={handleConfirmImport}
              >
                {commit.isLoading
                  ? `Creating recovery import...`
                  : `Confirm and create recovery import`}
              </button>
            ) : null}
            <button
              type="button"
              disabled={isBusy}
              className="inline-flex min-h-11 items-center justify-center gap-2 rounded-lg border border-border bg-background px-4 py-2 font-display text-sm font-bold text-foreground shadow-sm transition-colors hover:bg-surface disabled:cursor-not-allowed disabled:text-muted"
              onClick={resetSelection}
            >
              {committedOk ? `Upload another file` : `Choose a different file`}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export const GreenbuttonSupportDiagnostics: FC<
  GreenbuttonSupportDiagnosticsProps
> = ({ connectionId, siteId }) => {
  const diagnostic = useArcadiaGreenButtonSupportDiagnostic(
    siteId,
    connectionId,
    true,
  )
  const recovery = useRecoverArcadiaGreenButtonSupportConnection()

  if (diagnostic.isLoading) {
    return (
      <section aria-label="Green Button support diagnostics">
        <p className="text-sm font-semibold text-muted">
          Loading support diagnostics...
        </p>
      </section>
    )
  }

  if (diagnostic.isError || !diagnostic.data) {
    return (
      <section aria-label="Green Button support diagnostics">
        <p className="text-sm font-semibold text-danger">
          Support diagnostics are unavailable.
        </p>
      </section>
    )
  }

  const { data } = diagnostic
  const resources = [
    ...data.resources.credentials.map((resource) => ({
      ...resource,
      kind: `Credential`,
    })),
    ...data.resources.accounts.map((resource) => ({
      ...resource,
      kind: `Account`,
    })),
    ...data.resources.meters.map((resource) => ({ ...resource, kind: `Meter` })),
  ]
  const recoveryError = recovery.isError
    ? parseAxiosError(
        recovery.error,
        `Recovery could not be dispatched. Refresh diagnostics and try again.`,
      )
    : null

  const handleRecovery = () => {
    recovery.mutate({ connectionId, requestId: crypto.randomUUID(), siteId })
  }

  return (
    <section aria-label="Green Button support diagnostics">
      <div className="rounded-xl border border-border bg-surface p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-muted">
              Vision Admin
            </p>
            <h3 className="mt-1 font-display text-lg font-bold text-foreground">
              Support diagnostics
            </h3>
          </div>
          <span className="rounded-full border border-primary/20 bg-primary/5 px-3 py-1 text-sm font-semibold text-primary">
            {STAGE_LABELS[data.stage]}
          </span>
        </div>

        <dl className="mt-5 grid grid-cols-2 gap-4 text-sm">
          <div>
            <dt className="font-semibold text-muted">Connection</dt>
            <dd className="mt-1 font-semibold text-foreground">
              {data.connectionStatus}
            </dd>
          </div>
          <div>
            <dt className="font-semibold text-muted">Last synchronized</dt>
            <dd className="mt-1 font-semibold text-foreground">
              {formatDateTime(data.lastSyncedAt)}
            </dd>
          </div>
          <div>
            <dt className="font-semibold text-muted">Selected meter</dt>
            <dd className="mt-1 font-semibold text-foreground">
              {data.selectedMeter
                ? (data.selectedMeter.maskedIdentifier ??
                  `Selected — identifier unavailable`)
                : `Not selected`}
            </dd>
          </div>
          <div>
            <dt className="font-semibold text-muted">Current import</dt>
            <dd className="mt-1 font-semibold text-foreground">
              {data.currentImport
                ? `${Math.round(data.currentImport.annualKwh).toLocaleString()} kWh · ${data.currentImport.quality}`
                : `Not available`}
              {data.currentImport?.transport === `admin_csv_recovery` ? (
                <span className="mt-0.5 block text-xs font-semibold text-muted">
                  Recovered via Admin CSV
                </span>
              ) : null}
            </dd>
          </div>
        </dl>

        {data.currentImport?.channelEvidence ? (
          <div className="mt-5">
            <h4 className="text-sm font-bold text-foreground">
              Per-channel evidence
            </h4>
            <p className="mt-1 text-sm text-muted">
              Finite, missing, provider-imputed, and Vision-imputed interval
              counts. Raw readings are never shown.
            </p>
            <ul className="mt-2 divide-y divide-border rounded-lg border border-border bg-background">
              {(
                [
                  [`importKwh`, `Import`],
                  [`exportKwh`, `Export`],
                  [`netKwh`, `Net`],
                ] as const
              ).map(([channel, label]) => {
                const evidence = data.currentImport!.channelEvidence![channel]
                const visionMethods =
                  evidence.visionImputationMethods?.length > 0
                    ? ` (${evidence.visionImputationMethods.join(`, `)})`
                    : ``
                return (
                  <li
                    key={channel}
                    className="grid gap-1 px-3 py-2 text-sm sm:grid-cols-[6rem_1fr]"
                  >
                    <span className="font-semibold text-foreground">
                      {label}
                    </span>
                    <span className="text-muted">
                      {evidence.finiteCount.toLocaleString()} finite ·{` `}
                      {evidence.missingCount.toLocaleString()} missing ·{` `}
                      {evidence.providerImputedCount.toLocaleString()} provider
                      imputed ·{` `}
                      {(evidence.visionImputedCount ?? 0).toLocaleString()}
                      {` `}Vision imputed{visionMethods} ·{` `}
                      {evidence.completeLocalDayCount.toLocaleString()} complete
                      local days
                    </span>
                  </li>
                )
              })}
            </ul>
          </div>
        ) : null}

        {data.isStale || data.actionableFailureReason ? (
          <div className="mt-5 flex gap-3 rounded-lg border border-warning/40 bg-warning/10 p-4 text-sm text-foreground">
            <ExclamationTriangleIcon
              aria-hidden="true"
              className="mt-0.5 size-5 shrink-0 text-warning"
            />
            <p>
              {data.actionableFailureReason ??
                `This connection has not synchronized within the expected window.`}
            </p>
          </div>
        ) : null}

        <div className="mt-5">
          <h4 className="text-sm font-bold text-foreground">
            Sanitized Arcadia resources
          </h4>
          {resources.length > 0 ? (
            <ul className="mt-2 divide-y divide-border rounded-lg border border-border bg-background">
              {resources.map((resource) => (
                <li
                  key={`${resource.kind}-${resource.id}`}
                  className="flex items-center justify-between gap-4 px-3 py-2 text-sm"
                >
                  <span className="font-semibold text-foreground">
                    {resource.kind} {resource.maskedIdentifier ?? ``}
                  </span>
                  <span className="text-right text-muted">
                    {resource.status}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="mt-2 text-sm text-muted">
              No Arcadia resources discovered yet.
            </p>
          )}
        </div>

        {data.latestProcessingOutcome ? (
          <p className="mt-4 text-sm text-muted">
            Latest outcome:{` `}
            <span className="font-semibold text-foreground">
              {data.latestProcessingOutcome.eventType}
            </span>
            {` · ${data.latestProcessingOutcome.status}`}
          </p>
        ) : null}

        {data.canRecover ? (
          <button
            type="button"
            aria-busy={recovery.isLoading}
            disabled={recovery.isLoading}
            className="mt-5 inline-flex min-h-11 items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2 font-display text-sm font-bold text-primary-foreground shadow-sm transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:bg-surface disabled:text-muted"
            onClick={handleRecovery}
          >
            <ArrowPathIcon aria-hidden="true" className="size-5" />
            {recovery.isLoading ? `Resuming...` : `Resume current operation`}
          </button>
        ) : null}

        {recovery.isSuccess ? (
          <p
            aria-live="polite"
            className="mt-3 text-sm font-semibold text-success"
          >
            Recovery dispatched: {recovery.data.operation.replaceAll(`_`, ` `)}
            {recovery.data.duplicate ? ` (already requested)` : ``}
          </p>
        ) : recoveryError ? (
          <p
            aria-live="polite"
            className="mt-3 text-sm font-semibold text-danger"
          >
            {recoveryError}
          </p>
        ) : null}

        {data.canCsvRecover ? (
          <ArcadiaCsvRecoverySection
            connectionId={connectionId}
            siteId={siteId}
          />
        ) : null}
      </div>
    </section>
  )
}
